<template>
    <canvas></canvas>
</template>

<script>
    import { mapState } from 'vuex';
    import { TweenMax, Power3 } from 'gsap/TweenMax';
    import * as PIXI from 'pixi.js';
    import calculateAspectRatio from '@/utilities/calculateAspectRatio';

    export default {
        name: 'myScene',

        computed: {
            ...mapState('window', {
                isTouch: 'isTouch',
                winHeight: 'height',
                width: 'width',
            }),
        },

        created() {
            this.isReady = false;
            this.isAnimEnter = true;

            this.one = { x: 4, y: -4 };
            this.two = { x: -4, y: 4 };
            this.three = { x: 4, y: -4 };
            this.four = { x: -4, y: 4 };
        },

        mounted() {
            // Global var
            this.height = (this.width < 1024) ? this.winHeight : this.winHeight * 1.2;
            this.fontSize = (this.width >= 1024) ? 20.84 : 33;
            this.gap = (this.width >= 1024) ? this.width/36 : this.width/26;
            this.factor = (this.width >= 1024) ? 3.55 : 4;

            // Create PIXI SCENE
            // -----------------

            this.app = new PIXI.Application({
                view: this.$el,
                width: this.width,
                height: this.height,
                antialias: false,
                transparent: false,
                resolution: window.devicePixelRatio,
                autoResize: true,
                backgroundColor: 0x162c83,
            });

            // this.app.stage.alpha = 0;

            const loader = PIXI.loader;
                  loader.add('paper', '/static/images/paper.jpg');

            loader.load((loader, resources) => {
                this.createBg();
                this.createText();
                this.createDisplacementFilter(resources.paper.texture);
                this.createMask();

                this.$nextTick(() => {
                    this.isReady = true;

                    this.animIntro();
                    this.$eventHub.$emit('scene:ready');
                })
            });

            this.$eventHub.$on('update', this.update);
            this.$eventHub.$on('resize', this.resize);
        },

        beforeDestroy() {
            this.$eventHub.$off('update', this.update);
            this.$eventHub.$off('resize', this.resize);
        },

        methods: {
            animIntro() {
                TweenMax.to(this.one, 1.2, { x: 0, y: 0, ease: Power3.easeOut });
                TweenMax.to(this.two, 1.2, { x: 0, y: 0, ease: Power3.easeOut });
                TweenMax.to(this.three, 1.2, { x: 0, y: 0, ease: Power3.easeOut });
                TweenMax.to(this.four, 1.2, { x: 0, y: 0, ease: Power3.easeOut, onComplete: () => {
                    this.isAnimEnter = false;
                } });

                // TweenMax.to(this.app.stage, 1.2, { alpha: 1, ease: Power3.easeOut });
            },

            createBg() {
                this.bg = new PIXI.Graphics();
                this.bg.beginFill(0x162c83, 1);
                this.bg.drawRect(0, 0, this.width, this.height);
                this.bg.endFill();

                this.app.stage.addChild(this.bg);
            },

            createText() {
                this.textStyle = new PIXI.TextStyle({
                    fontFamily: 'DrukWide-Bold',
                    fontSize: this.fontSize*this.width/100 + 'px',
                    fontStyle: 'normal',
                    fontWeight: 'normal',
                    fill: '#ffffff',
                    wordWrap: false,
                });

                this.textStyleStroke = new PIXI.TextStyle({
                    fontFamily: 'DrukWide-Bold',
                    fontSize: this.fontSize*this.width/100 + 'px',
                    fontStyle: 'normal',
                    fontWeight: 'normal',
                    fill: 'transparent',
                    stroke: '#ffffff',
                    strokeThickness: 2,
                    wordWrap: false,
                });

                this.angle = -45 * Math.PI / 180; // 45 deg

                // Line One : Freelance

                this.lineOne = new PIXI.Text('FREELANCE', this.textStyle);
                this.lineOne.x = 0;
                this.lineOne.y = this.height/this.factor;
                this.lineOne.anchor.x = 0;
                this.lineOne.anchor.y = 0;
                this.lineOne.rotation = this.angle;
                this.lineOne.dirty = true;
                this.app.stage.addChild(this.lineOne);

                this.lineOne2 = new PIXI.Text('FREELANCE', this.textStyle);
                this.lineOne2.x = this.lineOne.x - this.lineOne.width * Math.cos(this.angle);
                this.lineOne2.y = this.lineOne.y - this.lineOne.width * Math.sin(this.angle);
                this.lineOne2.anchor.x = 0;
                this.lineOne2.anchor.y = 0;
                this.lineOne2.rotation = this.angle;
                this.lineOne2.dirty = true;
                this.app.stage.addChild(this.lineOne2);

                // Line two: Interactive

                this.lineTwo = new PIXI.Text('INTERACTIVE', this.textStyleStroke);
                this.lineTwo.x = this.lineOne.x + 0 + this.gap;
                this.lineTwo.y = this.lineOne.y + this.lineOne.height + this.gap;
                this.lineTwo.anchor.x = 0;
                this.lineTwo.anchor.y = 0;
                this.lineTwo.rotation = this.angle;
                this.app.stage.addChild(this.lineTwo);

                this.lineTwo2 = new PIXI.Text('INTERACTIVE', this.textStyleStroke);
                this.lineTwo2.x = this.lineTwo.x - this.lineTwo.width * Math.cos(this.angle);
                this.lineTwo2.y = this.lineTwo.y - this.lineTwo.width * Math.sin(this.angle);
                this.lineTwo2.anchor.x = 0;
                this.lineTwo2.anchor.y = 0;
                this.lineTwo2.rotation = this.angle;
                this.app.stage.addChild(this.lineTwo2);

                // Line three: Developer

                this.lineThree = new PIXI.Text('DEVELOPER', this.textStyle);
                this.lineThree.x = this.lineTwo.x + 0 + this.gap;
                this.lineThree.y = this.lineTwo.y + this.lineOne.height + this.gap;
                this.lineThree.anchor.x = 0;
                this.lineThree.anchor.y = 0;
                this.lineThree.rotation = this.angle;
                this.app.stage.addChild(this.lineThree);

                this.lineThree2 = new PIXI.Text('DEVELOPER', this.textStyle);
                this.lineThree2.x = this.lineThree.x - this.lineThree.width * Math.cos(this.angle);
                this.lineThree2.y = this.lineThree.y - this.lineThree.width * Math.sin(this.angle);
                this.lineThree2.anchor.x = 0;
                this.lineThree2.anchor.y = 0;
                this.lineThree2.rotation = this.angle;
                this.app.stage.addChild(this.lineThree2);

                // Line four: Paris

                this.lineFour = new PIXI.Text('PARIS', this.textStyle);
                this.lineFour.x = this.lineThree.x + 0 + this.gap // + 500;
                this.lineFour.y = this.lineThree.y + this.lineOne.height + this.gap // - 500;
                this.lineFour.anchor.x = 0;
                this.lineFour.anchor.y = 0;
                this.lineFour.rotation = this.angle;
                this.app.stage.addChild(this.lineFour);

                this.lineFour2 = new PIXI.Text('PARIS', this.textStyle);
                this.lineFour2.x = this.lineFour.x + this.lineFour.width * Math.cos(this.angle);
                this.lineFour2.y = this.lineFour.y + this.lineFour.width * Math.sin(this.angle);
                this.lineFour2.anchor.x = 0;
                this.lineFour2.anchor.y = 0;
                this.lineFour2.rotation = this.angle;
                this.app.stage.addChild(this.lineFour2);
            },

            createDisplacementFilter(texture) {
                let computedSize = calculateAspectRatio(1500, 1004, this.width, this.height, false);
                let offsetX = Math.round(this.width - computedSize.width) * .5;
                let offsetY = Math.round(this.height - computedSize.height) * .5;

                this.displacementSprite = new PIXI.Sprite(texture);
                this.displacementSprite.x = offsetX;
                this.displacementSprite.y = offsetY;
                this.displacementSprite.width = computedSize.width;
                this.displacementSprite.height = computedSize.height;
                // this.displacementSprite.scale.x = 1.3;
                // this.displacementSprite.scale.y = 1.3;
                this.displacementFilter  = new PIXI.filters.DisplacementFilter( this.displacementSprite );
                this.displacementFilter.scale.x = 0;
                this.displacementFilter.scale.y = 20;

                this.displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
                this.displacementFilter.autoFit = true;

                this.app.stage.addChild(this.displacementSprite);

                this.app.stage.filters = [ this.displacementFilter ];

                this.paperSprite = new PIXI.Sprite(texture);
                this.paperSprite.x = offsetX;
                this.paperSprite.y = offsetY;
                this.paperSprite.width = computedSize.width;
                this.paperSprite.height = computedSize.height;
                // this.paperSprite.scale.x = 1.3;
                // this.paperSprite.scale.y = 1.3;

                this.paperSprite.blendMode = PIXI.BLEND_MODES.MULTIPLY;

                this.app.stage.addChild(this.paperSprite);
            },

            createMask() {
                const maskHeight = this.winHeight * 0.6;

                this.canvasMask = document.createElement('canvas');
                this.canvasMask.width  = this.width;
                this.canvasMask.height = maskHeight;

                this.canvasMaskCtx = this.canvasMask.getContext('2d');

                var gradient = this.canvasMaskCtx.createLinearGradient(0, 0, 0, maskHeight);
                    gradient.addColorStop(0, "#000000");
                    gradient.addColorStop(1, "#FFFFFF");

                this.canvasMaskCtx.fillStyle = gradient;
                this.canvasMaskCtx.fillRect(0, 0, this.width, maskHeight);

                this.maskBW = new PIXI.Sprite(PIXI.Texture.fromCanvas(this.canvasMask));
                this.maskBW.x = 0;
                this.maskBW.y = this.height - maskHeight;

                this.container = new PIXI.Container();
                this.container.y = 4;

                this.mask = new PIXI.Graphics();
                this.mask.y = this.height - maskHeight;
                this.mask.beginFill(0x162c83, 1);
                this.mask.drawRect(0, 0, this.width, maskHeight);
                this.mask.endFill();

                this.container.addChild(this.mask);
                this.container.addChild(this.maskBW);
                this.container.mask = this.maskBW;

                this.app.stage.addChild(this.container);
            },

            resize() {
                if (!this.isReady) return;

                if (this.isTouch && this.width === this.app.screen.width) return;

                // Global var
                this.height = (this.width < 1024) ? this.winHeight : this.winHeight * 1.2;
                this.fontSize = (this.width >= 1024) ? 20.84 : 30;
                this.factor = (this.width >= 1024) ? 3.55 : 4.55;

                this.height = this.winHeight * 1.2;
                this.app.renderer.resize(this.width,this.height);

                 // UPDATE Bg
                // --------------
                this.bg.beginFill(0x162c83, 1);
                this.bg.drawRect(0, 0, this.width, this.height);
                this.bg.endFill();


                // UPDATE Text
                // --------------
                this.gap = this.width/36;

                this.textStyle.fontSize = this.fontSize*this.width/100 + 'px';
                this.textStyleStroke.fontSize = this.fontSize*this.width/100 + 'px';

                this.lineOne.x = 0;
                this.lineOne.y = this.height/3.55;

                this.lineOne2.x = this.lineOne.x - this.lineOne.width * Math.cos(this.angle);
                this.lineOne2.y = this.lineOne.y - this.lineOne.width * Math.sin(this.angle);

                this.lineTwo.x = this.lineOne.x + 0 + this.gap;
                this.lineTwo.y = this.lineOne.y + this.lineOne.height + this.gap;

                this.lineTwo2.x = this.lineTwo.x - this.lineTwo.width * Math.cos(this.angle);
                this.lineTwo2.y = this.lineTwo.y - this.lineTwo.width * Math.sin(this.angle);

                this.lineThree.x = this.lineTwo.x + 0 + this.gap;
                this.lineThree.y = this.lineTwo.y + this.lineOne.height + this.gap;

                this.lineThree2.x = this.lineThree.x - this.lineThree.width * Math.cos(this.angle);
                this.lineThree2.y = this.lineThree.y - this.lineThree.width * Math.sin(this.angle);

                this.lineFour.x = this.lineThree.x + 0 + this.gap // + 500;
                this.lineFour.y = this.lineThree.y + this.lineOne.height + this.gap // - 500;

                this.lineFour2.x = this.lineFour.x + this.lineFour.width * Math.cos(this.angle);
                this.lineFour2.y = this.lineFour.y + this.lineFour.width * Math.sin(this.angle);


                // UPDATE DisplacementFilter
                // --------------
                let computedSize = calculateAspectRatio(1500, 1004, this.width, this.height, false);
                let offsetX = Math.round(this.width - computedSize.width) * .5;
                let offsetY = Math.round(this.height - computedSize.height) * .5;

                this.displacementSprite.x = offsetX;
                this.displacementSprite.y = offsetY;
                this.displacementSprite.width = computedSize.width;
                this.displacementSprite.height = computedSize.height;

                this.paperSprite.x = offsetX;
                this.paperSprite.y = offsetY;
                this.paperSprite.width = computedSize.width;
                this.paperSprite.height = computedSize.height;


                // UPDATE MASK
                // --------------
                const maskHeight = this.winHeight * 0.6;

                this.canvasMask.width  = this.width;
                this.canvasMask.height = maskHeight;

                this.canvasMaskCtx.clearRect(0, 0, this.canvasMask.width, this.canvasMask.height);

                var gradient = this.canvasMaskCtx.createLinearGradient(0, 0, 0, maskHeight);
                    gradient.addColorStop(0, "#000000");
                    gradient.addColorStop(1, "#FFFFFF");

                this.canvasMaskCtx.fillStyle = gradient;
                this.canvasMaskCtx.fillRect(0, 0, this.width, maskHeight);

                this.maskBW.texture.update();

                this.maskBW.y = this.height - maskHeight;

                this.mask.y = this.height - maskHeight;
                this.mask.beginFill(0x162c83, 1);
                this.mask.drawRect(0, 0, this.width, maskHeight);
                this.mask.endFill();

            },

            update() {
                if(this.$root.smoothScroll > this.height || !this.lineOne) return;

                let delta = Math.abs(this.$root.scrollTop - this.$root.smoothScroll) / 100;

                const speed = -0.1 - delta;
                const speed2 = -speed;

                this.lineOne.x = (this.lineOne.x - this.one.x) + speed;
                this.lineOne.y = (this.lineOne.y - this.one.y) - speed;
                this.lineOne2.x = (this.lineOne2.x - this.one.x) + speed;
                this.lineOne2.y = (this.lineOne2.y - this.one.y) - speed;

                this.lineTwo.x = (this.lineTwo.x - this.two.x) + speed2;
                this.lineTwo.y = (this.lineTwo.y - this.two.y) - speed2;
                this.lineTwo2.x = (this.lineTwo2.x - this.two.x) + speed2;
                this.lineTwo2.y = (this.lineTwo2.y - this.two.y) - speed2;

                this.lineThree.x = (this.lineThree.x - this.three.x) + speed;
                this.lineThree.y = (this.lineThree.y - this.three.y) - speed;
                this.lineThree2.x = (this.lineThree2.x - this.three.x) + speed;
                this.lineThree2.y = (this.lineThree2.y - this.three.y) - speed;

                this.lineFour.x  = (this.lineFour.x - this.four.x) + speed2;
                this.lineFour.y  = (this.lineFour.y - this.four.y) - speed2;
                this.lineFour2.x  = (this.lineFour2.x - this.four.x) + speed2;
                this.lineFour2.y  = (this.lineFour2.y - this.four.y) - speed2;

                // Culling + reposition word
                // --------------

                // Line One
                if(this.lineOne.y + this.lineOne.width * Math.sin(this.angle) > this.height) {
                    this.lineOne.x = this.lineOne2.x + this.lineOne2.width * Math.cos(this.angle);
                    this.lineOne.y = this.lineOne2.y + this.lineOne2.width * Math.sin(this.angle);
                }
                if(this.lineOne2.y + this.lineOne2.width * Math.sin(this.angle) > this.height) {
                    this.lineOne2.x = this.lineOne.x + this.lineOne.width * Math.cos(this.angle);
                    this.lineOne2.y = this.lineOne.y + this.lineOne.width * Math.sin(this.angle);
                }

                // Line two
                if(this.lineTwo.y + this.lineTwo.height < 0) {
                    this.lineTwo.x = this.lineTwo2.x - this.lineTwo2.width * Math.cos(this.angle);
                    this.lineTwo.y = this.lineTwo2.y - this.lineTwo2.width * Math.sin(this.angle);
                }
                if(this.lineTwo2.y + this.lineTwo2.height < 0) {
                    this.lineTwo2.x = this.lineTwo.x - this.lineTwo.width * Math.cos(this.angle);
                    this.lineTwo2.y = this.lineTwo.y - this.lineTwo.width * Math.sin(this.angle);
                }

                // Line Three
                if(this.lineThree.y + this.lineThree.width * Math.sin(this.angle) > this.height) {
                    this.lineThree.x = this.lineThree2.x + this.lineThree2.width * Math.cos(this.angle);
                    this.lineThree.y = this.lineThree2.y + this.lineThree2.width * Math.sin(this.angle);
                }
                if(this.lineThree2.y + this.lineThree2.width * Math.sin(this.angle) > this.height) {
                    this.lineThree2.x = this.lineThree.x + this.lineThree.width * Math.cos(this.angle);
                    this.lineThree2.y = this.lineThree.y + this.lineThree.width * Math.sin(this.angle);
                }

                 // Line four
                if(this.lineFour.y + this.lineFour.height < 0 || this.lineFour.x > this.width) {
                    this.lineFour.x = this.lineFour2.x - this.lineFour2.width * Math.cos(this.angle);
                    this.lineFour.y = this.lineFour2.y - this.lineFour2.width * Math.sin(this.angle);
                }
                if(this.lineFour2.y + this.lineFour2.height < 0 || this.lineFour2.x > this.width) {
                    this.lineFour2.x = this.lineFour.x - this.lineFour.width * Math.cos(this.angle);
                    this.lineFour2.y = this.lineFour.y - this.lineFour.width * Math.sin(this.angle);
                }
            },
        }
    };
</script>